import { useTranslation } from 'react-i18next';
import { useUserHasRole } from '#pie/auth/useUserHasRole';
import { Card } from '#pie/components/card/Card';
import { DescriptionList } from '#pie/components/description-list/DescriptionList';
import { DescriptionListItem } from '#pie/components/description-list/description-list-item/DescriptionListItem';
import { InfoTooltip } from '#pie/components/info-tooltip/InfoTooltip';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import type { MarketPartyRegistration } from './marketPartyRegistrationAdapter';

interface MarketPartyCompanyProps {
  marketParty: MarketPartyRegistration;
}

export const MarketPartyCompany = ({
  marketParty,
}: MarketPartyCompanyProps) => {
  const { t } = useTranslation('marketParty');
  const showAddress = useUserHasRole({
    marketRole: ['LV', 'GDS', 'RNB', 'LNB'],
  });

  return (
    <Card
      size="md"
      heading={
        <Stack direction="row" gap="sm" align="center">
          <Text as="h2" variant="h6" className="text-content-heading">
            {t('company.title')}
          </Text>
          <InfoTooltip
            className="text-content-heading"
            aria-label="marketparty information is from the kvk"
            size={20}
          >
            {t('company.aria-label')}
          </InfoTooltip>
        </Stack>
      }
    >
      <DescriptionList variant="grid">
        <DescriptionListItem
          title={t('company.statutory_name')}
          direction="vertical"
        >
          {marketParty.statutoryName}
        </DescriptionListItem>
        <DescriptionListItem title={t('company.kvk')} direction="vertical">
          {marketParty.chamberOfCommerceNumber}
        </DescriptionListItem>
        {showAddress && (
          <DescriptionListItem
            title={t('company.address')}
            direction="vertical"
          >
            <Text>
              {marketParty.streetName} {marketParty.houseNumber}
            </Text>
            <Text>
              {marketParty.zipCode} {marketParty.city}
            </Text>
            <Text>{marketParty.country}</Text>
          </DescriptionListItem>
        )}
      </DescriptionList>
    </Card>
  );
};
